import React, { useMemo, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import moment, { Moment } from 'moment';
import { Link } from 'react-router-dom';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { Button } from '../components/Button';
import { PeriodToggle } from '../components/PeriodToggle';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { EmptyState } from '../components/EmptyState';
import { Pagination } from '../components/Pagination';
import {
  useArticleFeed,
  useArticleHeader,
  ArticleFilter,
  ArticleFilterAPI,
  formatArticleFilterAPI,
  useArticles
} from 'apiTyped/hooks/articles';
import {
  usePostSetReadMutation,
  usePostSetFavoriteMutation
} from 'apiTyped/hooks/post.mutations';
import get from 'lodash/get';

// --- Constants ---

const networkToPage: Record<string, string> = {
  ON: '/distribution/restaurants',
  OFF: '/distribution/restaurants',
  WEB: '/distribution/online-sales/overview',
  PRI: '/distribution/primeur',
  AUC: '/distribution/auction'
};

const networkLabel: Record<string, string> = {
  ON: 'ON',
  OFF: 'OFF',
  WEB: 'WEB',
  PRI: 'PRI',
  AUC: 'AUC'
};

// --- Press Review Page ---

export const V2PressReview: React.FC = () => {
  // Filters state
  const [filterValue, setFilterValue] = useState<ArticleFilter>({
    fromDate: moment().subtract(5, 'months'),
    toDate: moment(),
    page: 1
  });
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [readFilter, setReadFilter] = useState<string>('all');

  const filters: ArticleFilterAPI = useMemo(
    () =>
      formatArticleFilterAPI({
        ...filterValue,
        page,
        search: searchTerm || undefined,
        isRead:
          readFilter === 'read'
            ? true
            : readFilter === 'unread'
              ? false
              : undefined
      }),
    [filterValue, page, searchTerm, readFilter]
  );

  const { articlesResponse, isLoadingArticles } = useArticles(filters);
  const { articleFeed } = useArticleFeed();
  const { articleHeader } = useArticleHeader();

  // Highlighted (featured) articles
  const { articlesResponse: highlightedResponse } = useArticles({
    isFeatured: true,
    limit: 10
  });
  const highlightedArticles = get(highlightedResponse, 'results', []) as any[];
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [showHighlighted, setShowHighlighted] = useState(true);

  const articles = get(articlesResponse, 'results', []) as any[];
  const totalPages = get(articlesResponse, 'total_pages', 0);
  const totalCount = get(articlesResponse, 'count', 0);
  const articleNumber = articleHeader?.nb_articles;
  const feedArticles = articleFeed?.surveys || [];

  const handlePrevious = useCallback(() => {
    if (page > 1) setPage(page - 1);
  }, [page]);

  const handleNext = useCallback(() => {
    if (page < totalPages) setPage(page + 1);
  }, [page, totalPages]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  const handleDateChange = useCallback(
    (field: 'fromDate' | 'toDate', value: string) => {
      setFilterValue((prev) => ({
        ...prev,
        [field]: moment(value)
      }));
      setPage(1);
    },
    []
  );

  return (
    <Page>
      <PageHeader
        title="Press Review"
        subtitle="Your media coverage and articles feed"
      />

      {/* KPI Strip */}
      <KpiStrip
        items={[
          { label: 'Total articles', value: articleNumber ?? '—' },
          { label: 'This period', value: totalCount },
          { label: 'Featured', value: feedArticles.length },
          { label: 'Current page', value: `${page} / ${totalPages || 1}` }
        ]}
      />

      {/* Featured Articles Carousel */}
      {feedArticles.length > 0 && (
        <FeaturedSection>
          <SectionLabel>Featured</SectionLabel>
          <FeaturedGrid>
            {feedArticles.slice(0, 3).map((article: any, idx: number) => (
              <FeaturedCardLink
                key={idx}
                to={
                  networkToPage[article.network_code] || '/communication/media'
                }
              >
                <FeaturedCard>
                  <FeaturedBadge />
                  <FeaturedCardContent>
                    <FeaturedTitle>
                      {article.title}
                      {article.network_code &&
                        ` - ${
                          networkLabel[article.network_code] ||
                          article.network_code
                        }`}
                    </FeaturedTitle>
                    {article.date && (
                      <FeaturedDate>
                        {moment(article.date).format('DD/MM/YYYY')}
                      </FeaturedDate>
                    )}
                  </FeaturedCardContent>
                </FeaturedCard>
              </FeaturedCardLink>
            ))}
          </FeaturedGrid>
        </FeaturedSection>
      )}

      {/* Highlighted Articles */}
      {highlightedArticles.length > 0 && (
        <HighlightedSection>
          <HighlightedHeader>
            <SectionLabelHighlighted>
              Highlighted Articles
            </SectionLabelHighlighted>
            <HideToggle onClick={() => setShowHighlighted(!showHighlighted)}>
              {showHighlighted ? '⊘ Hide' : '⊘ Show'}
            </HideToggle>
          </HighlightedHeader>
          {showHighlighted && (
            <>
              <HighlightedGrid>
                <HighlightedMain>
                  {(() => {
                    const mainArticle = highlightedArticles[highlightedIndex];
                    if (!mainArticle) return null;
                    const imageUrl = get(mainArticle, 'content.image_url');
                    const title = get(mainArticle, 'content.title', '');
                    const body = get(mainArticle, 'content.body', '').replace(
                      /<[^>]+>/g,
                      ''
                    );
                    const source =
                      get(mainArticle, 'content.source_name', '') ||
                      get(mainArticle, 'source_name', '');
                    const category =
                      get(mainArticle, 'content.category', '') ||
                      get(mainArticle, 'category_name', '');
                    const date =
                      mainArticle.published_at || mainArticle.created_at;
                    return (
                      <>
                        {imageUrl && (
                          <HighlightedImage src={imageUrl} alt={title} />
                        )}
                        {category && (
                          <HighlightedCategory>{category}</HighlightedCategory>
                        )}
                        <HighlightedMainTitle>{title}</HighlightedMainTitle>
                        <HighlightedMeta>
                          {date && moment(date).format('D MMM YYYY')}
                          {source && <> from {source}</>}
                        </HighlightedMeta>
                        {body && (
                          <HighlightedExcerpt>
                            {body.slice(0, 200)}…
                          </HighlightedExcerpt>
                        )}
                        <HighlightedReadLink
                          to={`/communication/media/article/${mainArticle.id}/`}
                        >
                          Read the article
                        </HighlightedReadLink>
                      </>
                    );
                  })()}
                </HighlightedMain>
                <HighlightedSide>
                  {highlightedArticles
                    .slice(0, 6)
                    .filter((_, i) => i !== highlightedIndex)
                    .slice(0, 5)
                    .map((article: any, idx: number) => {
                      const imageUrl = get(article, 'content.image_url');
                      const title = get(article, 'content.title', '');
                      const source =
                        get(article, 'content.source_name', '') ||
                        get(article, 'source_name', '');
                      const date = article.published_at || article.created_at;
                      return (
                        <HighlightedSideItem
                          key={article.id || idx}
                          onClick={() =>
                            setHighlightedIndex(
                              highlightedArticles.indexOf(article)
                            )
                          }
                        >
                          {imageUrl && (
                            <HighlightedSideImage src={imageUrl} alt={title} />
                          )}
                          <HighlightedSideContent>
                            <HighlightedSideTitle>{title}</HighlightedSideTitle>
                            <HighlightedSideMeta>
                              {date && moment(date).format('D MMM YYYY')}
                              {source && <> from{source}</>}
                            </HighlightedSideMeta>
                          </HighlightedSideContent>
                        </HighlightedSideItem>
                      );
                    })}
                </HighlightedSide>
              </HighlightedGrid>
              <HighlightedNav>
                <NavButton
                  onClick={() =>
                    setHighlightedIndex(Math.max(0, highlightedIndex - 1))
                  }
                  disabled={highlightedIndex === 0}
                >
                  ‹
                </NavButton>
                <NavButton
                  onClick={() =>
                    setHighlightedIndex(
                      Math.min(
                        highlightedArticles.length - 1,
                        highlightedIndex + 1
                      )
                    )
                  }
                  disabled={highlightedIndex === highlightedArticles.length - 1}
                >
                  ›
                </NavButton>
              </HighlightedNav>
            </>
          )}
        </HighlightedSection>
      )}

      {/* Filters */}
      <FiltersBar>
        <SearchInput
          placeholder="Search articles..."
          value={searchTerm}
          onChange={handleSearch}
        />
        <FilterGroup>
          <DateInput
            type="date"
            value={filterValue.fromDate?.format('YYYY-MM-DD') || ''}
            onChange={(e) => handleDateChange('fromDate', e.target.value)}
          />
          <DateSeparator>to</DateSeparator>
          <DateInput
            type="date"
            value={filterValue.toDate?.format('YYYY-MM-DD') || ''}
            onChange={(e) => handleDateChange('toDate', e.target.value)}
          />
        </FilterGroup>
        <PeriodToggle
          options={[
            { label: 'All', value: 'all' },
            { label: 'Unread', value: 'unread' },
            { label: 'Read', value: 'read' }
          ]}
          value={readFilter}
          onChange={(v) => {
            setReadFilter(v);
            setPage(1);
          }}
        />
      </FiltersBar>

      {/* Article List */}
      {isLoadingArticles ? (
        <LoadingWrap>Loading articles...</LoadingWrap>
      ) : articles.length === 0 ? (
        <EmptyState message="No articles found for the current filters." />
      ) : (
        <ArticleListSection>
          {articles.map((article: any) => (
            <ArticleRow key={article.id} article={article} />
          ))}
        </ArticleListSection>
      )}

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </Page>
  );
};

// --- Article Row Component ---

const ArticleRow: React.FC<{ article: any }> = ({ article }) => {
  const setRead = usePostSetReadMutation(article.id);
  const setFavorite = usePostSetFavoriteMutation(article.id);

  const handleToggleFavorite = useCallback(() => {
    setFavorite.mutate();
  }, [setFavorite]);

  const handleMarkRead = useCallback(() => {
    setRead.mutate();
  }, [setRead]);

  const title = get(article, 'content.title', 'Untitled');
  const body = get(article, 'content.body', '');
  const source =
    get(article, 'content.source_name', '') || get(article, 'source_name', '');
  const country =
    get(article, 'content.country_name', '') ||
    get(article, 'country_name', '');
  const date = article.published_at || article.created_at;
  const tags = article.tags || [];
  const isRead = article.is_read;
  const isFavorite = article.is_favorite;

  // Strip HTML for preview
  const plainBody = body.replace(/<[^>]+>/g, '').slice(0, 200);

  return (
    <ArticleCard $isRead={isRead}>
      <ArticleCardLeft>
        <ArticleMeta>
          {source && <ArticleSource>{source}</ArticleSource>}
          {country && <ArticleCountry>{country}</ArticleCountry>}
          {date && (
            <ArticleDate>{moment(date).format('D MMM YYYY')}</ArticleDate>
          )}
        </ArticleMeta>
        <ArticleTitleLink to={`/communication/media/article/${article.id}/`}>
          {title}
        </ArticleTitleLink>
        {plainBody && <ArticlePreview>{plainBody}…</ArticlePreview>}
        {tags.length > 0 && (
          <TagRow>
            {tags.map((tag: any) => (
              <TagPill key={tag.id}>{tag.name}</TagPill>
            ))}
          </TagRow>
        )}
      </ArticleCardLeft>
      <ArticleCardRight>
        <ActionIcon
          onClick={handleToggleFavorite}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          $active={isFavorite}
        >
          ★
        </ActionIcon>
        {!isRead && (
          <ActionIcon
            onClick={handleMarkRead}
            title="Mark as read"
            $active={false}
          >
            ●
          </ActionIcon>
        )}
      </ArticleCardRight>
    </ArticleCard>
  );
};

// --- Styled Components ---

const Page = styled.div`
  max-width: 1400px;
`;

const FeaturedSection = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.div`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-faint);
  font-weight: 600;
  margin-bottom: 10px;
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  ${media.md} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeaturedCardLink = styled(Link)`
  text-decoration: none;
`;

const FeaturedCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const FeaturedBadge = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: var(--terracotta-soft, #f9dfd6);
`;

const FeaturedCardContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FeaturedTitle = styled.div`
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--ink);
  line-height: 1.3;
  margin-bottom: 4px;
`;

const FeaturedDate = styled.div`
  font-size: 13px;
  font-family: var(--font-sans);
  color: var(--terracotta);
  font-weight: 500;
`;

// --- Highlighted Articles Styled Components ---

const HighlightedSection = styled.div`
  margin-bottom: 24px;
`;

const HighlightedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SectionLabelHighlighted = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--terracotta);
  font-weight: 700;
`;

const HideToggle = styled.button`
  background: none;
  border: none;
  color: var(--ink-faint);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    color: var(--ink);
  }
`;

const HighlightedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  ${media.md} {
    grid-template-columns: 1.2fr 1fr;
  }
`;

const HighlightedMain = styled.div`
  display: flex;
  flex-direction: column;
`;

const HighlightedImage = styled.img`
  width: 100%;
  height: 260px;
  object-fit: cover;
  border-radius: var(--radius-md);
  margin-bottom: 14px;
`;

const HighlightedCategory = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  font-weight: 600;
  margin-bottom: 6px;
`;

const HighlightedMainTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 22px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.3;
  margin: 0 0 8px;
`;

const HighlightedMeta = styled.div`
  font-size: 12px;
  color: var(--ink-faint);
  margin-bottom: 10px;
`;

const HighlightedExcerpt = styled.p`
  font-size: 13px;
  color: var(--ink-muted);
  line-height: 1.5;
  margin: 0 0 12px;
`;

const HighlightedReadLink = styled(Link)`
  font-size: 13px;
  color: var(--ink);
  font-weight: 500;
  text-decoration: underline;
`;

const HighlightedSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const HighlightedSideItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
  padding: 4px;
  border-radius: var(--radius-sm);
  transition: background 0.15s;

  &:hover {
    background: var(--paper);
  }
`;

const HighlightedSideImage = styled.img`
  width: 100px;
  height: 70px;
  object-fit: cover;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
`;

const HighlightedSideContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const HighlightedSideTitle = styled.div`
  font-family: var(--font-serif);
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.3;
  margin-bottom: 4px;
`;

const HighlightedSideMeta = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
`;

const HighlightedNav = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: var(--white);
  color: ${(p) => (p.disabled ? 'var(--ink-faint)' : 'var(--ink)')};
  font-size: 18px;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.15s;

  &:hover:not(:disabled) {
    box-shadow: var(--shadow-sm);
  }
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateInput = styled.input`
  border: 1px solid var(--line);
  background: var(--white);
  border-radius: var(--radius-sm);
  padding: 6px 10px;
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--ink);
  outline: none;

  &:focus {
    border-color: var(--terracotta);
  }
`;

const DateSeparator = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
`;

const LoadingWrap = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

const ArticleListSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ArticleCard = styled.div<{ $isRead: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 16px 18px;
  transition:
    box-shadow 0.15s,
    border-color 0.15s;
  opacity: ${({ $isRead }) => ($isRead ? 0.75 : 1)};

  &:hover {
    box-shadow: var(--shadow-sm);
    border-color: var(--ink-faint);
  }
`;

const ArticleCardLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const ArticleCardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
  flex-shrink: 0;
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`;

const ArticleSource = styled.span`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--terracotta);
  font-weight: 600;
`;

const ArticleCountry = styled.span`
  font-size: 11px;
  color: var(--ink-faint);
  padding: 1px 6px;
  background: var(--paper);
  border-radius: 3px;
`;

const ArticleDate = styled.span`
  font-size: 11px;
  color: var(--ink-faint);
`;

const ArticleTitleLink = styled(Link)`
  display: block;
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  color: var(--ink);
  text-decoration: none;
  margin-bottom: 4px;
  line-height: 1.3;

  &:hover {
    color: var(--terracotta);
  }
`;

const ArticlePreview = styled.p`
  font-size: 12.5px;
  color: var(--ink-muted);
  line-height: 1.5;
  margin: 0 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const TagPill = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: var(--radius-pill);
  background: var(--paper);
  color: var(--ink-muted);
  border: 1px solid var(--line-soft);
`;

const ActionIcon = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--ink-faint)'};
  transition: color 0.15s;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: var(--terracotta);
  }
`;
